<template>
  <div class="container">
      <LoadingSpinner :loading="loading"> </LoadingSpinner>

      <div v-if="!loading" class="mt-4 mb-4 refund-page">
        <h3>Refusjon for ordre betalt hver for seg</h3>
        <div class="mt-4">{{getOrderTypeText()}} - Ordre #{{order.orderId}}</div>

        <!-- Step 1: Payment and Product Selection -->
        <div v-if="step == 1">
          <div class="mt-4">
            <div class="alert alert-info">
              ℹ️ <strong>Ordre betalt hver for seg:</strong> Velg produkter fra én betaling å refundere. Du kan bare refundere fra én betaling om gangen.<br>
              <small class="text-muted">Du kan refundere delvis - ikke alle betalinger trenger å refunderes. Trykk 'Ferdig' når du er ferdig.</small><br>
              <span v-if="processedPayments.length > 0" class="text-success small">
                ✓ {{ processedPayments.length }} av {{ order.payments.length }} betalinger er refundert.
                <span v-if="processedPayments.length === order.payments.length"> Alle betalinger er refundert!</span>
              </span>
            </div>
          </div>
          
          <div class="payment-selection mt-3">
            <div v-for="payment in order.payments" :key="payment.id" 
                 :class="['payment-item', 'mb-4', { 'payment-active': activePaymentId === payment.id, 'payment-disabled': isPaymentDisabled(payment), 'payment-processed': isPaymentProcessed(payment) }]">
              <!-- Payment Header -->
              <div class="payment-header clickable-row" @click="!isPaymentDisabled(payment) && togglePaymentSelection(payment)">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <input type="checkbox" :name="payment.id" :value="payment.id" 
                           :checked="isPaymentFullySelected(payment)" 
                           :disabled="isPaymentDisabled(payment)"
                           @click.stop="togglePaymentSelection(payment)"
                           class="checkbox payment-checkbox" />
                    <div>
                      <div><strong>{{ getPaymentTypeDisplayName(payment.paymentType) }}</strong></div>
                      <div v-if="isPaymentProcessed(payment)" class="text-success small">✓ Refundert</div>
                    </div>
                  </div>
                  <div>
                    <strong>{{ displayTwoDecimals(payment.amount) }}</strong>
                  </div>
                </div>
              </div>

              <!-- Cart Items for this Payment -->
              <div class="payment-items mt-3" v-if="payment.cartItems && payment.cartItems.length > 0">
                <div>
                  <div class="small text-muted mb-2">Produkter i denne betalingen:</div>
                  <div v-for="cartItem in getRefundCartItemsForPayment(payment)" :key="cartItem.id" class="cart-item-row clickable-row" @click="!isPaymentDisabled(payment) && toggleCartItemSelection(cartItem, payment)">
                    <div class="d-flex align-items-center justify-content-between">
                      <div class="d-flex align-items-center">
                        <input type="checkbox" :name="cartItem.id" :value="cartItem.id" 
                               :checked="isCartItemSelected(cartItem, payment)" 
                               :disabled="isPaymentDisabled(payment)"
                               @click.stop="toggleCartItemSelection(cartItem, payment)"
                               class="checkbox-sm" />
                        <div class="ms-2">
                          <div class="item-name">{{ cartItem.itemName }}</div>
                          <div v-if="cartItem.itemSubselection" class="text-muted small">
                            {{ cartItem.itemSubselection.name }}
                          </div>
                          <div v-if="cartItem.spicyLevel && cartItem.spicyLevel !== ''" class="text-muted small">
                            {{ cartItem.spicyLevel }}
                          </div>
                          <div v-if="cartItem.comment && cartItem.comment !== ''" class="text-muted small">
                            {{ cartItem.comment }}
                          </div>
                        </div>
                      </div>
                      <div class="item-price">
                        {{ displayTwoDecimals(getPriceWithVatAndManualDiscount(cartItem, payment)) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tips for this payment (if any) -->
              <div v-if="payment.tipsAmount && payment.tipsAmount > 0" class="payment-tips mt-2">
                <div class="d-flex align-items-center justify-content-between clickable-row" @click="!isPaymentDisabled(payment) && togglePaymentTips(payment)">
                  <div class="d-flex align-items-center">
                    <input type="checkbox" :name="'tips_' + payment.id" :value="'tips_' + payment.id"
                           :checked="isPaymentTipsSelected(payment)"
                           :disabled="isPaymentDisabled(payment)"
                           @click.stop="togglePaymentTips(payment)"
                           class="checkbox-sm" />
                    <div class="ms-2 small">Tips</div>
                  </div>
                  <div class="item-price">
                    {{ displayTwoDecimals(payment.tipsAmount) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="bo_savebtn_container mt-5 mb-5">
            <div class="d-flex justify-content-between">
              <div>
                <button v-if="processedPayments.length > 0" type="button" class="btn btn-outline-success" @click="finishRefundProcess">
                  Ferdig
                </button>
              </div>
              <div>
                <button type="button" class="bo_btn" @click="goToStep2" :disabled="!hasAnySelections()">Neste</button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="step == 2">
          <hr class="mb-0"/>
          <div v-for="selectedCartItem in selectedCartItems" :key="selectedCartItem.id" :id="selectedCartItem.id" class="refundCartItem">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <div>
                  <div>
                    -1 x {{selectedCartItem.itemName}}
                  </div>
                  <div v-if="selectedCartItem.itemSubselection">
                    {{ selectedCartItem.itemSubselection.name }}
                  </div>
                  <div v-if="selectedCartItem.spicyLevel != ''">
                    {{ selectedCartItem.spicyLevel}}
                  </div>
                  <div v-if="selectedCartItem.comment != ''">
                    {{ selectedCartItem.comment }}
                  </div>
                </div>
              </div>
              <div>
                -{{ displayTwoDecimals(getPriceWithVatAndManualDiscount(selectedCartItem)) }}
              </div>
            </div>
          </div>
          <div v-if="refundedTipsAmount > 0" class="mt-2 mb-3">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <div> -Tips </div>
              </div>
              <div>
                -{{ displayTwoDecimals(refundedTipsAmount) }}
              </div>
            </div>
          </div>

          <div class="d-flex align-items-center justify-content-between">
            <div>
              <span style="font-weight: bold;">Totalt</span>
            </div>
            <div>
              <span style="font-weight: bold;color: red">-{{ displayTwoDecimals(getTotalRefundAmount()) }}</span>
            </div>
          </div>

          <!-- Payment distribution info -->
          <div class="final-summary mt-4">
            <div class="alert alert-info">
              <h6>📋 Oppsummering av refusjon:</h6>
              <div><strong>Total beløp:</strong> {{ displayTwoDecimals(getTotalRefundAmount()) }}</div>
              <div class="mt-3">
                <strong>Refunderes til:</strong>
                <div class="mt-2">
                  <div v-for="refundPayment in refundDistribution.filter(p => p.refundAmount > 0)" :key="refundPayment.id" class="payment-refund-summary mb-3">
                    <div class="payment-refund-header">
                      <strong>{{ getPaymentTypeDisplayName(refundPayment.paymentType) }}: {{ displayTwoDecimals(refundPayment.refundAmount) }}</strong>
                    </div>
                    <div class="payment-refund-details ms-3 mt-1">
                      <div v-if="refundPayment.selectedItems && refundPayment.selectedItems.length > 0" class="text-muted small">
                        Produkter: {{ refundPayment.selectedItems.length }} stk for {{ displayTwoDecimals(refundPayment.refundAmount - refundPayment.selectedTips) }}
                      </div>
                      <div v-if="refundPayment.selectedTips > 0" class="text-muted small">
                        Tips: {{ displayTwoDecimals(refundPayment.selectedTips) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="mt-2 text-info">
                ℹ️ <strong>Hver betaling refunderes separat via kort terminal </strong>
              </div>
            </div>
          </div>

          <div class="bo_savebtn_container mt-5 mb-5">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <button type="button" class="bo_btn" @click="step=1">Tilbake</button>
              </div>
              <div>
                <button type="button" class="bo_btn" @click="executeRefund">Refunder</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import OrderService from "@/services/order/OrderService";
import OrderUtil from "@/util/OrderUtil";
import VatUtil from "@/util/VatUtil";
import ConfirmDialogue from "@/components/other/ConfirmDialogue";
import PayMyPartService from "@/services/payment/PayMyPartService";
import MathUtil from "@/util/MathUtil";
import RefundJournal from "@/util/RefundJournal";

export default {
  components: {LoadingSpinner, ConfirmDialogue},
  data() {
    return {
      loading: true,
      step: 1,

      vat: {},
      orderDocumentId: '',
      order: {
        type: "order",
        merchant: '',
        orderId: '',
        orderType: '',
        orderStatus: '',
        totalAmount: 0,
        description: '',
        pickupOrDeliveryTime: '',
        mobileNumber: '',
        name: '',
        nameOnDoorbell: '',
        street: '',
        houseNumber: '',
        floor: '',
        zipCode: '',
        city: '',
        orderedTime: '',
        modifiedTime: '',
        servedBy: '',
        channel: '',
        payments: [],
        refundedPayments: [], // Track all refunded payments
        vats: [],
        totalVat: 0,
        cartItemsJson: [],
        manualDiscount: 0
      },
      refundCartItems: [],
      refundedTipsAmount : 0,
      selectedCartItems: [],
      
      // Data for single payment refunds (one payment at a time)
      activePaymentId: null, // Only one payment can be active at a time
      selectedPayments: [],
      selectedCartItemsByPayment: {}, // Map of paymentId -> selected cart items
      selectedTipsByPayment: {}, // Map of paymentId -> boolean
      refundDistribution: [],
      refundExecutionStatus: [],
      processedPayments: [], // Track which payments have been refunded
      refundedPayment: null,
      
      // Promise resolve/reject functions for native app callbacks
      cardRefundResolve: null,
      cardRefundReject: null,
      currentRefundIndex: 0
    };
  },
  mounted() {
    document.getElementById('nav').hidden = true;
    document.getElementById('footer').hidden = true;
    document.getElementById('scrollTopBtn').hidden = true;

    //Define the method to handle messages from native Android app code
    window.receiveMessageFromNative = (message) => {
      try {
          const data = JSON.parse(message);
          if (data.status === 'REFUND_SUCCESS') {
            if (data.refundedPayment) {
              this.refundedPayment = JSON.parse(data.refundedPayment); //data.refundedPayment is string
            }
            // Card refund succeeded - resolve the promise
            if(this.cardRefundResolve){
              this.cardRefundResolve();
              this.cardRefundResolve = null;
              this.cardRefundReject = null;
            }
          } else {
            // Card refund failed or cancelled - reject the promise
            if(this.cardRefundReject){
              const errorMessage = data.status === 'REFUND_CANCELLED' ? 'Card refund cancelled by user' : 'Card refund failed';
              this.cardRefundReject(new Error(errorMessage));
              this.cardRefundResolve = null;
              this.cardRefundReject = null;
            } else {
              // Fallback for cases where we're not in a card refund flow
              this.loading = false;
              this.showRefundFailedMessage();
            }
          }
      } catch(error) {
        this.loading = false;
        this.showRefundFailedMessage();
      }
    };

    this.orderDocumentId = this.$route.query.orderDocumentId;
    OrderService.getDataForOrderDetailGui(this.orderDocumentId).then(
        (response) => {
          let dataForGui = response.data;
          //console.log('dataForGui: ' + JSON.stringify(dataForGui));
          this.order = dataForGui.order;
          this.vat = dataForGui.vat;
          this.refundCartItems = this.getRefundCartItems();
          this.initializeRefundExecutionStatus();
          
          // Initialize selection structures
          if(this.order.payments && this.order.payments.length > 0){
            this.initializeSelections();
          }
          
          this.loading = false;
        },
        (error) => {
          console.log(error);
        }
    );
  },
  methods:{
    getRefundCartItems(){
      let refundCartItems = [];
      let cartItems = this.order.cartItems;
      console.log('cartItems: ' + JSON.stringify(cartItems));
      for (let i = 0; i < cartItems.length; i++) {
        let cartItem = cartItems[i];
        for(let x = 1; x <= cartItem.count; x++){
          let refundCartItem = {
            id: cartItem.id + '_' + x, //make it unique
            itemId: cartItem.itemId,
            itemName: cartItem.itemName,
            itemType: cartItem.itemType,
            price: cartItem.price,
            discountPercent: cartItem.discountPercent ? cartItem.discountPercent : 0,
            count: 1,
            itemSubselection: cartItem.itemSubselection,
            spicyLevel: cartItem.spicyLevel,
            comment: cartItem.comment,
            createdDate: cartItem.createdDate
          };
          refundCartItems.push(refundCartItem);

          //subselection addOns
          if(refundCartItem.itemSubselection){
            let subselectionAddOnCartItems =  refundCartItem.itemSubselection.addOnCartItems;
            if(subselectionAddOnCartItems){
              for(let a = 0; a < subselectionAddOnCartItems.length; a++){
                let addOnCartItem = subselectionAddOnCartItems[a];
                let addOn = addOnCartItem.addOn;
                for(let y = 1; y <= addOnCartItem.count; y++){
                  //Only if it isn't free
                  if(addOnCartItem.price > 0){
                    let addOnRefundCartItem = {
                      id: cartItem.id + '_' + x + '_' + addOnCartItem.addOnId + '_' + y, //make it unique
                      itemId: addOnCartItem.addOnId,
                      itemName: addOn.name,
                      itemType: 'ADD_ON_OF_ADD_ON_GROUP',
                      price: addOnCartItem.price,
                      discountPercent: cartItem.discountPercent ? cartItem.discountPercent : 0, //The same as cartItem.discountPercent
                      count: 1,
                      createdDate: cartItem.createdDate
                    };
                    refundCartItems.push(addOnRefundCartItem);
                  }
                }
              }
            }
          }

          //item addOns
          if(cartItem.addOnCartItems && cartItem.addOnCartItems.length > 0){
            for (let a = 0; a < cartItem.addOnCartItems.length; a++) {
              let addOnCartItem = cartItem.addOnCartItems[a];
              let addOn = addOnCartItem.addOn;
              for(let y = 1; y <= addOnCartItem.count; y++){
                //Only if it isn't free
                if(addOnCartItem.price > 0){
                  let addOnRefundCartItem = {
                    id: cartItem.id + '_' + x + '_' + addOnCartItem.addOnId + '_' + y, //make it unique
                    itemId: addOnCartItem.addOnId,
                    itemName: addOn.name,
                    itemType: 'ADD_ON_OF_ADD_ON_GROUP',
                    price: addOnCartItem.price,
                    discountPercent: cartItem.discountPercent ? cartItem.discountPercent : 0, //The same as cartItem.discountPercent
                    count: 1,
                    createdDate: cartItem.createdDate
                  };
                  refundCartItems.push(addOnRefundCartItem);
                }
              }
            }
          }
        }
      }
      return refundCartItems;
    },

    getOrderTypeText(){
      return OrderUtil.getOrderTypeText(this.order.orderType).toUpperCase();
    },
    getPriceWithVat(cartItem){
      let vatRate = VatUtil.getVatRate(this.vat, this.order.orderType, cartItem.itemType);
      let priceWithVat = OrderUtil.getPriceWithVat(vatRate, cartItem.price) * (100 - cartItem.discountPercent) / 100;
      if(cartItem.discountPercent == 0){
        return Math.round(priceWithVat); //make it exact as it is in order description.
      } else {
        return MathUtil.roundTwoDecimals(priceWithVat);
      }
    },
    getPriceWithVatAndManualDiscount(cartItem, payment = null){
      // If no payment is specified, find it
      if(!payment){
        payment = this.findPaymentForCartItem(cartItem);
        if(!payment){
          return this.getPriceWithVat(cartItem); // Fallback to base price
        }
      }
      
      // For pay-my-part orders, use the exact price distribution from the payment
      // The prices in payment.cartItems already have manual discount applied
      return this.getExactPriceFromPayment(cartItem, payment);
    },
    getExactPriceFromPayment(cartItem, payment){
      // Get all cart items for this payment
      let paymentCartItems = this.getRefundCartItemsForPayment(payment);
      
      // Calculate expected total without tips
      let expectedProductTotal = payment.amount - (payment.tipsAmount || 0);
      
      // Calculate sum of all base prices for this payment
      let totalBasePrice = 0;
      paymentCartItems.forEach(item => {
        totalBasePrice += this.getPriceWithVat(item);
      });
      
      // If there's only one item, it gets the full expected amount
      if(paymentCartItems.length === 1){
        return MathUtil.roundTwoDecimals(expectedProductTotal);
      }
      
      // Find which item index this is
      let itemIndex = paymentCartItems.findIndex(item => item.id === cartItem.id);
      if(itemIndex === -1){
        return this.getPriceWithVat(cartItem); // Fallback
      }
      
      // For multiple items, distribute proportionally but adjust last item for exact match
      let basePrice = this.getPriceWithVat(cartItem);
      let proportionalPrice = (basePrice / totalBasePrice) * expectedProductTotal;
      
      // If this is the last item, calculate what's left to ensure exact match
      if(itemIndex === paymentCartItems.length - 1){
        let alreadyAllocated = 0;
        for(let i = 0; i < paymentCartItems.length - 1; i++){
          let otherItem = paymentCartItems[i];
          let otherBasePrice = this.getPriceWithVat(otherItem);
          let otherProportional = (otherBasePrice / totalBasePrice) * expectedProductTotal;
          alreadyAllocated += MathUtil.roundTwoDecimals(otherProportional);
        }
        return MathUtil.roundTwoDecimals(expectedProductTotal - alreadyAllocated);
      }
      
      return MathUtil.roundTwoDecimals(proportionalPrice);
    },
    findPaymentForCartItem(cartItem){
      // Find which payment this cart item belongs to
      for(let payment of this.order.payments){
        if(payment.cartItems){
          let refundCartItems = this.getRefundCartItemsForPayment(payment);
          let found = refundCartItems.find(item => item.id === cartItem.id);
          if(found){
            return payment;
          }
        }
      }
      return null;
    },
    initializeSelections(){
      // Create new reactive objects to ensure proper reactivity
      const cartItemsByPayment = {};
      const tipsByPayment = {};
      
      // Initialize empty selections for each payment
      this.order.payments.forEach(payment => {
        cartItemsByPayment[payment.id] = [];
        tipsByPayment[payment.id] = false;
      });
      
      this.selectedCartItemsByPayment = cartItemsByPayment;
      this.selectedTipsByPayment = tipsByPayment;
      this.selectedPayments = [];
      this.activePaymentId = null; // No payment is active initially
      this.processedPayments = []; // No payments processed yet
    },
    goToStep2(){
      // Consolidate all selected items for step 2 display and processing
      this.consolidateSelections();
      this.calculateRefundDistribution();
      this.step = 2;
    },
    consolidateSelections(){
      this.selectedCartItems = [];
      this.refundedTipsAmount = 0;
      
      // Collect all selected cart items from all payments
      Object.values(this.selectedCartItemsByPayment).forEach(items => {
        this.selectedCartItems = this.selectedCartItems.concat(items);
      });
      
      // Sum up all selected tips
      this.refundedTipsAmount = Object.entries(this.selectedTipsByPayment)
        .filter(([, selected]) => selected)
        .reduce((total, [paymentId]) => {
          const payment = this.order.payments.find(p => p.id === paymentId);
          return total + (payment ? (payment.tipsAmount || 0) : 0);
        }, 0);
    },
    getRefundCartItemsForPayment(payment){
      if(!payment.cartItems) return [];
      
      let refundCartItems = [];
      for (let i = 0; i < payment.cartItems.length; i++) {
        let cartItem = payment.cartItems[i];
        for(let x = 1; x <= cartItem.count; x++){
          let refundCartItem = {
            id: payment.id + '_' + cartItem.id + '_' + x, //make it unique per payment
            paymentId: payment.id,
            originalCartItemId: cartItem.id,
            itemId: cartItem.itemId,
            itemName: cartItem.itemName,
            itemType: cartItem.itemType,
            price: cartItem.price,
            discountPercent: cartItem.discountPercent ? cartItem.discountPercent : 0,
            count: 1,
            itemSubselection: cartItem.itemSubselection,
            spicyLevel: cartItem.spicyLevel,
            comment: cartItem.comment,
            createdDate: cartItem.createdDate
          };
          refundCartItems.push(refundCartItem);

          // Add addOns if any (similar logic as in original getRefundCartItems)
          if(refundCartItem.itemSubselection){
            let subselectionAddOnCartItems = refundCartItem.itemSubselection.addOnCartItems;
            if(subselectionAddOnCartItems){
              for(let a = 0; a < subselectionAddOnCartItems.length; a++){
                let addOnCartItem = subselectionAddOnCartItems[a];
                if(addOnCartItem.price > 0){
                  let addOn = addOnCartItem.addOn;
                  for(let y = 1; y <= addOnCartItem.count; y++){
                    let addOnRefundCartItem = {
                      id: payment.id + '_' + cartItem.id + '_' + x + '_' + addOnCartItem.addOnId + '_' + y,
                      paymentId: payment.id,
                      originalCartItemId: cartItem.id,
                      itemId: addOnCartItem.addOnId,
                      itemName: addOn.name,
                      itemType: 'ADD_ON_OF_ADD_ON_GROUP',
                      price: addOnCartItem.price,
                      discountPercent: cartItem.discountPercent ? cartItem.discountPercent : 0,
                      count: 1,
                      createdDate: cartItem.createdDate
                    };
                    refundCartItems.push(addOnRefundCartItem);
                  }
                }
              }
            }
          }

          // Add item addOns if any
          if(cartItem.addOnCartItems && cartItem.addOnCartItems.length > 0){
            for (let a = 0; a < cartItem.addOnCartItems.length; a++) {
              let addOnCartItem = cartItem.addOnCartItems[a];
              if(addOnCartItem.price > 0){
                let addOn = addOnCartItem.addOn;
                for(let y = 1; y <= addOnCartItem.count; y++){
                  let addOnRefundCartItem = {
                    id: payment.id + '_' + cartItem.id + '_' + x + '_' + addOnCartItem.addOnId + '_' + y,
                    paymentId: payment.id,
                    originalCartItemId: cartItem.id,
                    itemId: addOnCartItem.addOnId,
                    itemName: addOn.name,
                    itemType: 'ADD_ON_OF_ADD_ON_GROUP',
                    price: addOnCartItem.price,
                    discountPercent: cartItem.discountPercent ? cartItem.discountPercent : 0,
                    count: 1,
                    createdDate: cartItem.createdDate
                  };
                  refundCartItems.push(addOnRefundCartItem);
                }
              }
            }
          }
        }
      }
      return refundCartItems;
    },
    isPaymentFullySelected(payment){
      const paymentItems = this.getRefundCartItemsForPayment(payment);
      const selectedItems = this.selectedCartItemsByPayment[payment.id] || [];
      const hasTips = payment.tipsAmount && payment.tipsAmount > 0;
      const tipsSelected = this.selectedTipsByPayment[payment.id] || false;
      
      return paymentItems.length > 0 && 
             paymentItems.every(item => selectedItems.some(selected => selected.id === item.id)) &&
             (!hasTips || tipsSelected);
    },
    isPaymentDisabled(payment){
      // A payment is disabled if another payment is active OR if it's already processed
      return (this.activePaymentId !== null && this.activePaymentId !== payment.id) || 
             this.isPaymentProcessed(payment);
    },
    isPaymentProcessed(payment){
      return this.processedPayments.includes(payment.id);
    },
    togglePaymentSelection(payment){
      const isFullySelected = this.isPaymentFullySelected(payment);
      const paymentItems = this.getRefundCartItemsForPayment(payment);
      
      if(isFullySelected){
        // Deselect all items and tips for this payment
        this.selectedCartItemsByPayment[payment.id] = [];
        this.selectedTipsByPayment[payment.id] = false;
        this.activePaymentId = null; // No payment is active now
      } else {
        // First clear any other active payments
        this.clearAllSelections();
        
        // Select all items and tips for this payment
        this.selectedCartItemsByPayment[payment.id] = [...paymentItems];
        if(payment.tipsAmount && payment.tipsAmount > 0){
          this.selectedTipsByPayment[payment.id] = true;
        }
        this.activePaymentId = payment.id; // This payment is now active
      }
      
      // Force reactivity by creating new objects
      this.selectedCartItemsByPayment = { ...this.selectedCartItemsByPayment };
      this.selectedTipsByPayment = { ...this.selectedTipsByPayment };
    },
    clearAllSelections(){
      // Clear all selections from all payments
      Object.keys(this.selectedCartItemsByPayment).forEach(paymentId => {
        this.selectedCartItemsByPayment[paymentId] = [];
        this.selectedTipsByPayment[paymentId] = false;
      });
    },
    isCartItemSelected(cartItem, payment){
      const selectedItems = this.selectedCartItemsByPayment[payment.id] || [];
      return selectedItems.some(selected => selected.id === cartItem.id);
    },
    toggleCartItemSelection(cartItem, payment){
      // If another payment is active, do nothing
      if(this.isPaymentDisabled(payment)) return;
      
      if(!this.selectedCartItemsByPayment[payment.id]){
        this.selectedCartItemsByPayment[payment.id] = [];
      }
      
      const selectedItems = [...this.selectedCartItemsByPayment[payment.id]];
      const index = selectedItems.findIndex(selected => selected.id === cartItem.id);
      
      if(index >= 0){
        selectedItems.splice(index, 1);
        // If no items are selected from this payment anymore, clear active payment
        if(selectedItems.length === 0 && !this.selectedTipsByPayment[payment.id]){
          this.activePaymentId = null;
        }
      } else {
        // Clear other payments if this is the first selection
        if(this.activePaymentId === null){
          this.clearAllSelections();
          this.activePaymentId = payment.id;
        }
        selectedItems.push(cartItem);
      }
      
      // Update with new array to trigger reactivity
      this.selectedCartItemsByPayment[payment.id] = selectedItems;
      this.selectedCartItemsByPayment = { ...this.selectedCartItemsByPayment };
    },
    isPaymentTipsSelected(payment){
      return this.selectedTipsByPayment[payment.id] || false;
    },
    togglePaymentTips(payment){
      // If another payment is active, do nothing
      if(this.isPaymentDisabled(payment)) return;
      
      const wasSelected = this.selectedTipsByPayment[payment.id];
      this.selectedTipsByPayment[payment.id] = !wasSelected;
      
      if(!wasSelected){
        // Selecting tips - make this payment active if no other is active
        if(this.activePaymentId === null){
          this.clearAllSelections();
          this.activePaymentId = payment.id;
          this.selectedTipsByPayment[payment.id] = true; // Re-apply since clearAllSelections cleared it
        }
      } else {
        // Deselecting tips - check if payment should still be active
        const hasSelectedItems = this.selectedCartItemsByPayment[payment.id] && this.selectedCartItemsByPayment[payment.id].length > 0;
        if(!hasSelectedItems){
          this.activePaymentId = null;
        }
      }
      
      this.selectedTipsByPayment = { ...this.selectedTipsByPayment };
    },
    hasAnySelections(){
      // Check if any cart items or tips are selected
      const hasSelectedItems = Object.values(this.selectedCartItemsByPayment).some(items => items.length > 0);
      const hasSelectedTips = Object.values(this.selectedTipsByPayment).some(selected => selected);
      return hasSelectedItems || hasSelectedTips;
    },
    getTotalRefundAmount(){
      let totalPricesInCart = 0.00;
      for (let i = 0; i < this.selectedCartItems.length; i++) {
        let selectedCartItem = this.selectedCartItems[i];
        let cartItemPrice = this.getPriceWithVatAndManualDiscount(selectedCartItem);
        console.log(cartItemPrice);
        totalPricesInCart = totalPricesInCart + cartItemPrice;
      }

      totalPricesInCart = totalPricesInCart + this.refundedTipsAmount;
      
      return totalPricesInCart;
    },
    initializeRefundExecutionStatus(){
      if(this.order.payments && this.order.payments.length > 0){
        this.refundExecutionStatus = this.order.payments.map(payment => ({
          paymentId: payment.id,
          executed: false,
          success: false
        }));
      }
    },
    calculateRefundDistribution(){
      this.refundDistribution = [];
      
      // Calculate refund amount for each payment based on selected items
      this.order.payments.forEach(payment => {
        const selectedItems = this.selectedCartItemsByPayment[payment.id] || [];
        const tipsSelected = this.selectedTipsByPayment[payment.id] || false;
        
        let paymentRefundAmount = 0;
        
        // Sum selected items for this payment
        selectedItems.forEach(item => {
          paymentRefundAmount += this.getPriceWithVatAndManualDiscount(item);
        });
        
        // Add tips if selected
        if(tipsSelected && payment.tipsAmount){
          paymentRefundAmount += payment.tipsAmount;
        }
        
        // Only add to distribution if there's something to refund
        if(paymentRefundAmount > 0){
          this.refundDistribution.push({
            id: payment.id,
            paymentType: payment.paymentType,
            originalAmount: payment.amount,
            refundAmount: paymentRefundAmount,
            transactionId: payment.transactionId,
            onlinePaymentProvider: payment.onlinePaymentProvider,
            selectedItems: selectedItems,
            selectedTips: tipsSelected ? payment.tipsAmount : 0,
            refundedCartItems: selectedItems,
            refundedPayment: this.refundedPayment
          });
        }
      });
    },
    getPaymentTypeDisplayName(paymentType){
      if(paymentType === 'CASH') return 'Kontant';
      if(paymentType === 'DEBIT_CARD' || paymentType === 'CREDIT_CARD') return 'Kort';
      if(paymentType === 'MOBILE_APP') return 'Vipps';
      if(paymentType.includes('vipps')) return 'Vipps';
      if(paymentType.includes('creditcard')) return 'Kort';
      if(paymentType === 'payex.applepay') return 'Apple Pay';
      return paymentType;
    },
    async executeRefund(){
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Refundere kr ' + this.displayTwoDecimals(this.getTotalRefundAmount()),
        message: 'Vil du refundere kr ' + this.displayTwoDecimals(this.getTotalRefundAmount()) + ' til kunden?',
        okButton: 'Refunder',
        cancelButton: 'Avbryt',
        spreadButtons: true,
      });
      
      if(!ok) return;
      
      this.loading = true;
      
      try {
        // Calculate refund distribution
        this.calculateRefundDistribution();
        
        // Execute refunds for each payment
        await this.executeSequentialRefunds();
        
        // Mark this payment as processed
        if(this.activePaymentId){
          this.processedPayments.push(this.activePaymentId);
        }
        
        // Check if all payments are processed
        if(this.processedPayments.length === this.order.payments.length){
          // All payments processed, go to order detail
          this.goToOrderDetailPage();
        } else {
          // More payments to process, go back to step 1
          this.returnToStep1AfterRefund();
        }
      } catch (error) {
        alert(JSON.stringify(error));

        console.error('Refund execution failed:', error);
        this.loading = false;
        
        // Don't show error dialog if user cancelled
        if (error.message !== 'Card refund cancelled by user') {
          await this.showRefundFailedMessage();
        }
      }
    },
    async executeSequentialRefunds(){
      // Execute refunds for each card payment sequentially
      const cardRefunds = this.refundDistribution.filter(p => p.refundAmount > 0);
      
      for(let i = 0; i < cardRefunds.length; i++){
        this.currentRefundIndex = i;
        await this.executeCardRefund(cardRefunds[i], i);
        
        // Record the refund in backend after successful card transaction
        await this.recordRefundInBackend(cardRefunds[i], i);
      }
    },
    async executeCardRefund(refund){
      return new Promise((resolve, reject) => {
        // Store the resolve/reject functions for the native callback
        this.cardRefundResolve = resolve;
        this.cardRefundReject = reject;
        
        // Open Softpay app on device for card refund
        this.openSoftpayAppOnDevice(this.order.orderId, refund.refundAmount, refund.refundedCartItems);
      });
    },
    async recordRefundInBackend(refund, index){
      try {
        refund.refundedPayment = this.refundedPayment; // Update refund object with the latest refundedPayment data received from native app
        
        const response = await PayMyPartService.refund(
            refund.refundAmount,
            refund.refundedPayment,
            refund.selectedItems, // Use the selected items for this specific payment
            refund.selectedTips, // Use the selected tips for this specific payment
            this.orderDocumentId
        );
        
        if(response.data === 'SUCCEEDED'){
          this.incrementGrandTotalRefund(refund.refundAmount, this.orderDocumentId);
          // One guest's payment back: a return of its own in the till's journal. The order's receipt number is not
          // this guest's sale, so it is left out.
          RefundJournal.send({
            order: this.order,
            orderDocumentId: this.orderDocumentId,
            rows: refund.selectedItems,
            priceOf: row => this.getPriceWithVatAndManualDiscount(row),
            paidBack: [{ paymentType: refund.paymentType, amount: refund.refundAmount, transactionId: refund.transactionId }],
            tips: refund.selectedTips,
            saleReceiptNr: null,
          });
          this.refundExecutionStatus[index].executed = true;
          this.refundExecutionStatus[index].success = true;
        } else {
          throw new Error('Recording refund failed for payment ' + refund.transactionId);
        }
      } catch (error) {
        this.refundExecutionStatus[index].executed = true;
        this.refundExecutionStatus[index].success = false;
        throw error;
      }
    },
    returnToStep1AfterRefund(){
      // Clear current selections
      this.activePaymentId = null;
      this.selectedCartItems = [];
      this.refundedTipsAmount = 0;
      this.refundDistribution = [];
      
      // Clear selection objects for all payments
      Object.keys(this.selectedCartItemsByPayment).forEach(paymentId => {
        this.selectedCartItemsByPayment[paymentId] = [];
        this.selectedTipsByPayment[paymentId] = false;
      });
      
      // Force reactivity
      this.selectedCartItemsByPayment = { ...this.selectedCartItemsByPayment };
      this.selectedTipsByPayment = { ...this.selectedTipsByPayment };
      
      // Go back to step 1
      this.step = 1;
      this.loading = false;
    },
    openSoftpayAppOnDevice(orderId, totalRefundAmount, refundedCartItems){
      if(window.JSBridge && window.JSBridge.openSoftpayApp) {
        window.JSBridge.openSoftpayApp(orderId, totalRefundAmount, JSON.stringify(refundedCartItems));
      }
    },
    incrementGrandTotalRefund(refundAmount, orderDocumentId){
      if(window.JSBridge && window.JSBridge.incrementGrandTotalRefund) {
        window.JSBridge.incrementGrandTotalRefund(refundAmount, orderDocumentId);
      }
    },
    goToOrderDetailPage(){
      this.$router.push({ path: 'orderDetail', query: {
          orderDocumentId: this.orderDocumentId,
          showRefundInfo: 'false'
        }
      });
    },
    finishRefundProcess(){
      // Allow user to finish the refund process without refunding all payments
      this.goToOrderDetailPage();
    },
    async showRefundFailedMessage(){
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Refusjonen mislykket',
        message: 'Det skjedde en feil og refusjonen ble ikke fullført.',
        okButton: 'OK',
        cancelButton: 'NOT_SET'
      })
      if(ok) {
        //Do nothing
      }
    },
    displayTwoDecimals(num){
      if(num > 0 || num < 0){
        return MathUtil.displayTwoDecimals(num);
      }else {
        return num;
      }
    }
  }
}
</script>

<style scoped>
  .refundCartItem{
    padding-top: 10px;
    padding-bottom: 10px;
    border-bottom: var(--bo-muted-2);
    border-bottom: 1px  solid var(--bo-muted-2);
  }

  .checkbox{
    width: 25px;
    height: 25px;
    margin-right: 10px;
  }

  /* Styles for payment distribution */
  .final-summary .alert {
    border-left: 4px solid #17a2b8;
  }

  .payment-refund-item {
    display: block;
    margin-bottom: 6px;
    margin-left: 15px;
    line-height: 1.4;
  }

  /* Payment selection styles */
  .payment-item {
    padding: 16px;
    border: 1px solid var(--bo-line);
    border-radius: 8px;
    margin-bottom: 16px;
    background-color: var(--bo-surface-2);
  }

  .payment-header {
    padding-bottom: 8px;
    border-bottom: 1px solid var(--bo-line);
  }

  .payment-checkbox {
    width: 20px;
    height: 20px;
    margin-right: 12px;
  }

  .cart-item-row {
    padding: 8px 0px 8px 0;
    border-bottom: 1px solid #f1f1f1;
  }

  .cart-item-row:last-child {
    border-bottom: none;
  }

  .checkbox-sm {
    width: 20px;
    height: 20px;
    margin-right: 8px;
  }

  .item-name {
    font-weight: 500;
    color: var(--bo-ink);
  }

  .item-price {
    font-weight: 500;
    color: var(--bo-muted);
  }

  .payment-tips {
    border-top: 1px solid var(--bo-line);
    padding-top: 8px;
    margin-left: 12px;
  }

  .payment-items {
    background-color: var(--bo-surface-2);
    border-radius: 4px;
    padding: 12px 0px 12px 0px;
    margin-left: 12px;
  }

  .payment-refund-summary {
    background-color: var(--bo-surface-2);
    border-left: 3px solid #007bff;
    padding: 8px 12px;
    border-radius: 4px;
  }

  .payment-refund-header {
    margin-bottom: 4px;
  }

  .payment-refund-details {
    font-size: 0.875rem;
  }

  /* Payment state styles */
  .payment-active {
    border-color: #007bff !important;
    background-color: var(--bo-info-bg) !important;
  }

  .payment-disabled {
    opacity: 0.5;
    background-color: var(--bo-surface-2) !important;
  }

  .payment-disabled input {
    cursor: not-allowed;
  }

  .payment-processed {
    border-color: #28a745 !important;
    background-color: var(--bo-surface-2) !important;
  }

  .payment-processed input {
    cursor: not-allowed;
  }

  /* Clickable row styles */
  .clickable-row {
    cursor: pointer;
  }

  .payment-disabled .clickable-row,
  .payment-processed .clickable-row {
    cursor: not-allowed;
  }
</style>
